/**
 * Solid-fill colours for the tag and topic chips in the v2026 chat-card tag row.
 *
 * resolveTagChipFill(raw) -> frozen { bg, fg, border } ('#RRGGBB' each) | null
 *
 * The tag colour is used as-is with a white label; a tag too light to read is fixed by recolouring
 * it in tag settings, not here. The border is the fill darkened 15% so a near-white tag keeps an edge.
 *
 * null means "not paintable": the chip keeps today's outline. That covers everything that is not
 * 3- or 6-digit hex — null, '', named colours, rgb()/hsl(), 4/8-digit hex, junk. A colourless
 * topic arrives as '' and must stay a teal outline, so never default a missing colour before
 * calling this.
 */

const TEXT = '#FFFFFF'
const BORDER_SCALE = 0.85
const CACHE_LIMIT = 500

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

// Keyed on the raw value as received, so null / '' / junk are cached too. Results are frozen and
// shared, which keeps the `fill` prop reference-equal between renders.
const cache = new Map()

function parseHex (raw) {
  if (typeof raw !== 'string') { return null }
  const match = HEX_RE.exec(raw.trim())
  if (!match) { return null }
  let hex = match[1]
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
}

function toHex (rgb) {
  return '#' + rgb.map(ch => ch.toString(16).padStart(2, '0')).join('').toUpperCase()
}

function compute (raw) {
  const rgb = parseHex(raw)
  if (!rgb) { return null }
  return {
    bg: toHex(rgb),
    fg: TEXT,
    border: toHex(rgb.map(ch => Math.round(ch * BORDER_SCALE)))
  }
}

export function resolveTagChipFill (raw) {
  if (cache.has(raw)) { return cache.get(raw) }
  const result = compute(raw)
  const frozen = result ? Object.freeze(result) : null
  if (cache.size >= CACHE_LIMIT) { cache.clear() }
  cache.set(raw, frozen)
  return frozen
}
